import React, { useState, useEffect } from 'react';
import { HiOutlinePlus, HiOutlineClipboardDocument, HiOutlineTrash, HiOutlinePencil, HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineXMark, HiOutlineQrCode } from 'react-icons/hi2';

import { db } from '../../config/firebaseConfig';
import { doc, updateDoc, getDocs, collection, query, where, addDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const AffiliateCodeManager = () => {
  const [loading, setLoading] = useState(true);
  const [affiliateCodes, setAffiliateCodes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentCode, setCurrentCode] = useState({ id: '', name: '', code: '', discount: 0, active: true });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [maxDiscount, setMaxDiscount] = useState(0);
  const { currentUser, userData } = useAuth();

  useEffect(() => {
    const fetchCodes = async () => {
      try {
        const codesCollection = collection(db, 'affiliateCodes');
        const codesQuery = query(codesCollection, where('affiliateId', '==', currentUser.uid));
        const codesSnapshot = await getDocs(codesQuery);
        const codesList = codesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMaxDiscount(userData.maxApprovedDiscountAmount * 1);
        setAffiliateCodes(codesList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching codes:', error);
        setLoading(false);
      }
    };
    fetchCodes();
  }, []);

  const handleOpenDialog = (code = null) => {
    if (code) {
      setCurrentCode({ ...code });
      setEditMode(true);
    } else {
      setCurrentCode({ id: '', name: '', code: '', discount: 0, active: true });
      setEditMode(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCode({
      ...currentCode,
      [name]: name === 'discount' ? Number(value) : value
    });
    if (name === 'discount' && Number(value) > maxDiscount) {
      setSnackbar({ open: true, message: `Discount cannot exceed ${maxDiscount}%`, severity: 'error' });
    }
  };

  // Check if a code name or code value already exists in the database
  const checkDuplicateCode = async (name, code, excludeId = null) => {
    try {
      // Check for duplicate code name or code value across all affiliate codes
      const codesCollection = collection(db, 'affiliateCodes');
      const nameQuery = query(codesCollection, where('name', '==', name));
      const codeQuery = query(codesCollection, where('code', '==', code));
      
      const [nameSnapshot, codeSnapshot] = await Promise.all([
        getDocs(nameQuery),
        getDocs(codeQuery)
      ]);
      
      // Filter out the current code being edited if in edit mode
      const duplicateNames = nameSnapshot.docs
        .filter(doc => excludeId ? doc.id !== excludeId : true)
        .map(doc => ({ id: doc.id, ...doc.data() }));
        
      const duplicateCodes = codeSnapshot.docs
        .filter(doc => excludeId ? doc.id !== excludeId : true)
        .map(doc => ({ id: doc.id, ...doc.data() }));
      
      return {
        hasDuplicateName: duplicateNames.length > 0,
        hasDuplicateCode: duplicateCodes.length > 0
      };
    } catch (error) {
      console.error('Error checking for duplicate codes:', error);
      return { hasDuplicateName: false, hasDuplicateCode: false, error };
    }
  };
  
  const handleSaveCode = async () => {
    // Check for duplicate code name or value
    const { hasDuplicateName, hasDuplicateCode, error } = await checkDuplicateCode(
      currentCode.name, 
      currentCode.code,
      editMode ? currentCode.id : null
    );
    
    if (error) {
      setSnackbar({ open: true, message: 'Error checking for duplicates: ' + error.message, severity: 'error' });
      return;
    }

    if (currentCode.discount > maxDiscount) {
      setSnackbar({ open: true, message: 'Discount cannot exceed ' + maxDiscount + '%', severity: 'error' });
      return;
    }

    if (currentCode.discount < 5) {
      setSnackbar({ open: true, message: 'Discount cannot be less than 5%', severity: 'error' });
      return;
    }
    
    if (hasDuplicateName) {
      setSnackbar({ open: true, message: 'A code with this name already exists. Please choose a different name.', severity: 'error' });
      return;
    }
    
    if (hasDuplicateCode) {
      setSnackbar({ open: true, message: 'This code value is already in use. Please choose a different code.', severity: 'error' });
      return;
    }
    
    if (editMode) {
      const docRef = doc(db, 'affiliateCodes', currentCode.id);
      await updateDoc(docRef, {...currentCode, affiliateId: currentUser.uid, affiliatePercentage: maxDiscount - currentCode.discount});
      const updatedCodes = affiliateCodes.map(code => 
        code.id === currentCode.id ? { ...currentCode } : code
      );
      setAffiliateCodes(updatedCodes);
      setSnackbar({ open: true, message: 'Affiliate code updated successfully!', severity: 'success' });
      handleCloseDialog();
    } else {
      const newCode = {
        ...currentCode,
        id: Date.now().toString(),
        created: new Date().toISOString().split('T')[0],
        uses: 0,
        affiliateId: currentUser.uid,
        affiliatePercentage: maxDiscount - currentCode.discount
      };
      const docRef = await addDoc(collection(db, 'affiliateCodes'), newCode);
      await updateDoc(docRef, {id: docRef.id});
      newCode.id = docRef.id;
      setAffiliateCodes([...affiliateCodes, newCode]);
      setSnackbar({ open: true, message: 'New affiliate code created successfully!', severity: 'success' });

      const response2 = await axios.post('https://bosp-yachts-backend.vercel.app/api/mailchimp/send-email', {
        templateName: 'adm-affiliate-application',
        email: "info@bosphorusyachts.com",
        firstName: "Bosphorus",
        lastName: "Yachts",
        emailVars: [
          {
            name: 'subject',
            content: "New Affiliate Code Created"
          },
          {
            name: 'subtitle',
            content: userData.firstName + " " + userData.lastName + " created a new affiliate code"
          },
          {
            name: 'contentTitle',
            content: userData.firstName + " " + userData.lastName + " created new Affiliate Code: " + newCode.code 
          },
          {
            name: 'content',
            content: "Check the code in Admin Dashboard"
          },
        ]
      });
      console.log(response2);
      handleCloseDialog();
    }
    
  };

  const handleDeleteClick = (code) => {
    const docRef = doc(db, 'affiliateCodes', code.id);
    deleteDoc(docRef);
    setCodeToDelete(code);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    const updatedCodes = affiliateCodes.filter(code => code.id !== codeToDelete.id);
    setAffiliateCodes(updatedCodes);
    setOpenDeleteDialog(false);
    setSnackbar({ open: true, message: 'Affiliate code deleted successfully!', severity: 'success' });
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setSnackbar({ open: true, message: 'Affiliate code copied to clipboard!', severity: 'info' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 className="section-title">Affiliate Codes</h2>
          <p className="section-subtitle">Create and manage your affiliate codes. These codes can be shared with your audience to track referrals and earn commissions.</p>
        </div>
        <button className="btn-primary" onClick={() => handleOpenDialog()}>
          <HiOutlinePlus />
          Create New Code
        </button>
      </div>

      {affiliateCodes.length > 0 ? (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Discount (%)</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {affiliateCodes.map((code) => (
                <tr key={code.id}>
                  <td>{code.name}</td>
                  <td>
                    <div className="code-cell">
                      <span className="code-text">{code.code}</span>
                      <button className="copy-btn" onClick={() => handleCopyCode(code.code)} title="Copy code">
                        <HiOutlineClipboardDocument />
                      </button>
                    </div>
                  </td>
                  <td>{code.discount}%</td>
                  <td>
                    <span className={`status-badge ${code.active ? 'active' : 'inactive'}`}>
                      {code.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{code.created}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit" onClick={() => handleOpenDialog(code)} title="Edit">
                        <HiOutlinePencil />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDeleteClick(code)} title="Delete">
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <HiOutlineQrCode className="empty-state-icon" />
          <p>You haven't created any affiliate codes yet.</p>
          <button className="btn-primary" onClick={() => handleOpenDialog()}>
            <HiOutlinePlus />
            Create Your First Code
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {openDialog && (
        <div className="modal-overlay" onClick={handleCloseDialog}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editMode ? 'Edit Affiliate Code' : 'Create New Affiliate Code'}</h2>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Code Name</label>
                <input
                  type="text"
                  name="name"
                  value={currentCode.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Summer Campaign"
                />
                <span className="helper-text">A descriptive name for your reference</span>
              </div>
              <div className="form-group">
                <label>Affiliate Code</label>
                <input
                  type="text"
                  name="code"
                  value={currentCode.code}
                  onChange={handleInputChange}
                  placeholder="e.g., SUMMER25"
                />
                <span className="helper-text">The code your audience will use</span>
              </div>
              <div className="form-group">
                <label>Discount Percentage</label>
                <input
                  type="number"
                  name="discount"
                  value={currentCode.discount}
                  onChange={handleInputChange}
                  min={5}
                  max={maxDiscount}
                  placeholder="e.g., 10"
                />
                <span className="helper-text">Discount for customers (5-{maxDiscount}%)</span>
                <div className="commission-preview">
                  Your commission: {maxDiscount - currentCode.discount}%
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseDialog}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveCode}>
                {editMode ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {openDeleteDialog && (
        <div className="modal-overlay" onClick={() => setOpenDeleteDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Affiliate Code</h2>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the affiliate code "<strong>{codeToDelete?.name}</strong>"? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setOpenDeleteDialog(false)}>Cancel</button>
              <button className="btn-danger" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {snackbar.open && (
        <div className="toast-container">
          <div className={`toast ${snackbar.severity}`}>
            {snackbar.severity === 'success' ? <HiOutlineCheckCircle /> : snackbar.severity === 'error' ? <HiOutlineXCircle /> : <HiOutlineCheckCircle />}
            <span>{snackbar.message}</span>
            <button className="toast-close" onClick={handleCloseSnackbar}>
              <HiOutlineXMark />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateCodeManager;
